import { useEffect } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { useLocalize } from '@/i18n/localize'
import { ArrowLeft, ArrowUpRight } from 'lucide-react'
import { BrandLockup } from '@/components/brand/BrandLockup'
import { getBladeByRoute } from '@/services/blades/bladeRegistry'
import { trackEvent } from '@/lib/posthog'

/**
 * ToolLayout — shared chrome for every public tool/door page (the spider-web
 * legs). Gives all apps one consistent way back to the main BeetleSense site,
 * which matters on the tool subdomains (e.g. logscaling.beetlesense.ai), where
 * an internal "/" link would just loop back to the tool — so the link must be
 * an ABSOLUTE url to the apex.
 *
 * Wrapping the tool routes in this layout means every current and future tool
 * inherits the brand bar + footer automatically ("update all of them"). Dark
 * theme + design tokens per apps/web/DESIGN.md. Slightly larger base text for
 * readability (gloves/older eyes).
 */
const MAIN_SITE = 'https://beetlesense.ai'

export function ToolLayout() {
  const { L } = useLocalize()

  // Per-blade usage analytics: fire a host-tagged tool_view for the compute
  // blade at this route, so a subdomain visit (e.g. volume.beetlesense.ai) is
  // distinguishable from a /tools path hit. The /d/:id photo-scan doors
  // instrument their own view, and the /tools hub isn't a tool — so only
  // routes that resolve to a compute blade fire here (no double-count).
  const { pathname } = useLocation()
  useEffect(() => {
    const blade = getBladeByRoute(pathname)
    if (!blade) return
    trackEvent('tool_view', {
      tool: blade.id,
      surface: 'tool',
      host: typeof window !== 'undefined' ? window.location.hostname : undefined,
    })
  }, [pathname])

  return (
    <div className="verktyg-theme min-h-screen text-[1.05rem]">
      {/* Brand bar — links to the full platform (absolute, reaches the apex
          from any tool subdomain). */}
      <header
        className="sticky top-0 z-30 border-b print:hidden"
        style={{ background: 'var(--bg)', borderColor: 'var(--border)' }}
      >
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <a href={MAIN_SITE} className="group" aria-label="BeetleSense">
              <BrandLockup descriptor="FIELD TOOLS" glow size="sm" />
            </a>
            <span
              className="hidden sm:inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] leading-none uppercase"
              style={{
                fontFamily: 'var(--font-mono)',
                letterSpacing: '0.14em',
                color: 'var(--text3)',
                borderColor: 'var(--border)',
              }}
            >
              {L({ sv: 'Indikativa verktyg', en: 'Indicative tools' })}
            </span>
          </div>
          <div className="flex items-center gap-4">
            {/* Back to the tools hub — only on a specific tool route (not the hub
                itself or the photo-scan doors). Replaces the per-page nav bar so
                there's a single header, not two. */}
            {pathname.startsWith('/tools/') && (
              <Link
                to="/tools"
                className="inline-flex items-center gap-1 text-sm font-medium text-[var(--text3)] hover:text-[var(--green)] transition-colors"
                style={{ fontFamily: 'var(--font-mono)' }}
              >
                <ArrowLeft className="w-3.5 h-3.5" />
                {L({ sv: 'Alla verktyg', en: 'All tools' })}
              </Link>
            )}
            <a
              href={MAIN_SITE}
              className="inline-flex items-center gap-1 text-sm font-medium text-[var(--text3)] hover:text-[var(--green)] transition-colors"
            >
              {L({ sv: 'Hela plattformen', en: 'Full platform' })}
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </header>

      <Outlet />

      {/* Footer — second, explicit way back to the main site. */}
      <footer className="border-t print:hidden" style={{ borderColor: 'var(--border)' }}>
        <div className="max-w-2xl mx-auto px-5 sm:px-8 py-8 text-sm text-[var(--text3)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <span>
            {L({ sv: 'En del av BeetleSense — evidens för skogen.', en: 'Part of BeetleSense — evidence for forests.' })}
          </span>
          <div className="flex items-center gap-4">
            <Link
              to="/tools/method"
              className="font-medium text-[var(--text2)] hover:text-[var(--green)] transition-colors"
            >
              {L({ sv: 'Metod & källor', en: 'Method & sources' })}
            </Link>
            <a
              href={MAIN_SITE}
              className="inline-flex items-center gap-1 font-medium text-[var(--green)] hover:underline"
            >
              {L({ sv: 'Se hela plattformen', en: 'See the full platform' })}
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default ToolLayout
